// Analytics — tabbed view over the same contactAIAgentSession window.
//
// Tabs:
//   - Overview   — single-screen snapshot (outcome mix, concern mix,
//                  daily volume, top entry points, top agents)
//   - Business   — Queue health + Bot effectiveness scorecards
//   - Technical  — Caller journey funnel + Bot-tool invocation audit
//
// Date-range form is shared across tabs — fetchWindow() hits
// run-search-query for sessions + taskDetails + taskLegDetails once,
// and every tab projects its view from the same base dataset.
// Switching tabs is instant; only a date-range change re-fetches.
//
// Column visibility per report persists in sessionStorage (see
// ./reports/columnPrefs.js).

import React, { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconChartBar, IconHammer, IconRefresh } from '@tabler/icons-react';

import { fmtTs, toDatetimeLocal } from './utils';
import { useReportWindow } from './reports/useReportWindow';
import { AnalyticsOverview } from './reports/AnalyticsOverview';
import { QueueHealth } from './reports/QueueHealth';
import { BotEffectiveness } from './reports/BotEffectiveness';
import { FlowFunnel } from './reports/FlowFunnel';
import { BotToolAudit } from './reports/BotToolAudit';

export function Analytics() {
  const nowIso = toDatetimeLocal(new Date());
  const dayAgoIso = toDatetimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000));

  const [form, setForm] = useState({ from: dayAgoIso, to: nowIso });
  const [tab, setTab] = useState('overview');
  const { loading, data, error, fetchedAt, from, to, fetchWindow } = useReportWindow();

  const runFetch = useCallback(() => {
    const fromMs = new Date(form.from).getTime();
    const toMs = new Date(form.to).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) return;
    fetchWindow(fromMs, toMs);
  }, [form, fetchWindow]);

  return (
    <Stack>
      <div>
        <Title order={3}>Analytics</Title>
        <Text size="sm" c="dimmed">
          Session activity snapshot and operational reports. Pick a window, then switch tabs — the
          data is fetched once and projected into every view. Business tab is for supervisors;
          Technical tab is for engineers investigating bot behaviour.
        </Text>
      </div>

      <Paper withBorder p="md" radius="md">
        <Grid gutter="sm" align="flex-end">
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="From"
              type="datetime-local"
              value={form.from}
              onChange={(e) => setForm((f) => ({ ...f, from: e.currentTarget.value }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="To"
              type="datetime-local"
              value={form.to}
              onChange={(e) => setForm((f) => ({ ...f, to: e.currentTarget.value }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Group gap="xs">
              <Button
                size="xs"
                leftSection={<IconRefresh size={14} />}
                loading={loading}
                onClick={runFetch}
              >
                Compute
              </Button>
              {fetchedAt && (
                <Text size="xs" c="dimmed">
                  {data?.sessions.length ?? 0} sessions · {data?.legs.length ?? 0} legs · {fmtTs(fetchedAt, true)}
                </Text>
              )}
            </Group>
          </Grid.Col>
        </Grid>
      </Paper>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Fetch failed">
          <Text size="xs">{error}</Text>
        </Alert>
      )}
      {loading && <Loader size="sm" />}

      {!loading && data && (
        <>
          {(data.sessionsTruncated || data.tasksTruncated || data.legsTruncated) && (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Window truncated">
              At least one search root reported <strong>hasNextPage: true</strong>. The numbers below are a
              partial slice — narrow the window to get an accurate rollup.
            </Alert>
          )}

          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="overview" leftSection={<IconChartBar size={14} />}>Overview</Tabs.Tab>
              <Tabs.Tab value="business" leftSection={<IconChartBar size={14} />}>Business reports</Tabs.Tab>
              <Tabs.Tab value="technical" leftSection={<IconHammer size={14} />}>Technical reports</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <AnalyticsOverview sessions={data.sessions} />
            </Tabs.Panel>

            <Tabs.Panel value="business" pt="md">
              <Stack gap="md">
                <QueueHealth data={data} />
                <BotEffectiveness data={data} />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="technical" pt="md">
              <Stack gap="md">
                <FlowFunnel data={data} />
                <BotToolAudit from={from} to={to} />
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </>
      )}

      {!loading && !data && !error && (
        <Alert color="gray" variant="light">
          Pick a window above and hit <strong>Compute</strong> to populate the reports.
        </Alert>
      )}
    </Stack>
  );
}
